/*
 * Cart Analytics — SELF-CONTAINED, DOM-driven storefront collector.
 *
 * v3 (2026-07-08). Captures everything from DOM events the drawer already emits
 * to the document (clicks, changes), the <cart-drawer> 'active' class, and
 * upsell-card visibility. Deliberately does NOT rely on:
 *   - caEmit() calls inside cart-drawer.js  (wiped whenever the cart is republished)
 *   - wrapping window.fetch  (the drawer pins its own native fetch at load, so a
 *     wrapper never sees its cart calls — load-order dependent & fragile)
 * DOM-event delegation survives cart-drawer.js rebuilds as long as the stable
 * conventions hold: the <cart-drawer> element + 'active' class, data-e5-action
 * controls, .e5cd__upsell-card, and the #e5cd-checkout-form / [name=checkout].
 *
 * Transport: batched, fire-and-forget sendBeacon (text/plain → no preflight),
 * never blocks the cart, env-gated (live vs draft).
 */
(function () {
  'use strict';

  var root = document.getElementById('cart-analytics-config');
  if (!root) return;
  var ENDPOINT = root.getAttribute('data-ca-endpoint');
  var ENV = root.getAttribute('data-ca-env') || 'live';
  var SHOP = root.getAttribute('data-ca-shop') || '';
  var CA_DROP_DRAFT = root.getAttribute('data-ca-drop-draft') === 'true';
  if (!ENDPOINT) return;
  if (ENV !== 'live' && CA_DROP_DRAFT) return;
  if (window.__caCollectorLoaded) return;
  window.__caCollectorLoaded = true;

  var NATIVE_FETCH = window.fetch.bind(window); // pinned before anything can rewrap

  // ---- anonymous session id (cookie, 1st-party, no PII) ----
  function getSessionId() {
    var m = document.cookie.match(/(?:^|;\s*)ca_sid=([^;]+)/);
    if (m) return m[1];
    var sid = 's_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 10);
    var d = new Date(); d.setDate(d.getDate() + 180);
    document.cookie = 'ca_sid=' + sid + ';expires=' + d.toUTCString() + ';path=/;SameSite=Lax';
    return sid;
  }
  var SID = getSessionId();

  // ---- batch + transport ----
  var MAX_BYTES = 60000, FLUSH_MS = 4000, buffer = [], flushTimer = null;
  function flush(useBeacon) {
    if (!buffer.length) return;
    var batch = buffer; buffer = [];
    if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
    var body = JSON.stringify({ shop: SHOP, sessionId: SID, environment: ENV, events: batch });
    if (useBeacon && navigator.sendBeacon) {
      if (!navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'text/plain' }))) sendFetch(body);
    } else { sendFetch(body); }
  }
  function sendFetch(body) {
    try { NATIVE_FETCH(ENDPOINT, { method: 'POST', headers: { 'Content-Type': 'text/plain' }, body: body, keepalive: true, mode: 'cors', credentials: 'omit' }).catch(function () {}); } catch (e) {}
  }
  function emit(type, payload) {
    buffer.push({
      t: type, p: payload || {}, ts: new Date().toISOString(),
      cv: currentCart ? currentCart.total_price : null,
      ic: currentCart ? currentCart.item_count : null,
      ct: currentCart ? (currentCart.token || null) : null
    });
    if (JSON.stringify(buffer).length > MAX_BYTES) { var last = buffer.pop(); flush(true); buffer.push(last); }
    if (!flushTimer) flushTimer = setTimeout(function () { flush(true); }, FLUSH_MS);
  }

  // ---- lightweight cart snapshot (for value/token + gift-tier detection) ----
  var currentCart = null, maxGiftTier = 0, snapTimer = null;
  function refreshCart() {
    // GET /cart.js is never intercepted by the drawer; safe & cheap.
    NATIVE_FETCH('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { currentCart = cart; checkGiftTiers(cart); })
      .catch(function () {});
  }
  function scheduleCartRefresh() { clearTimeout(snapTimer); snapTimer = setTimeout(refreshCart, 600); }

  // gift thresholds come from the drawer's data island (data-goals JSON, pence)
  function giftThresholds() {
    try {
      var el = document.querySelector('cart-drawer .e5cd__data');
      var goals = el ? JSON.parse(el.getAttribute('data-goals') || '[]') : [];
      return goals.map(function (g) { return { threshold: g.threshold, reward_type: g.reward_type }; });
    } catch (e) { return []; }
  }
  function checkGiftTiers(cart) {
    if (!cart) return;
    var total = cart.total_price || 0;
    giftThresholds().forEach(function (g) {
      if (total >= g.threshold && g.threshold > maxGiftTier) {
        maxGiftTier = g.threshold;
        emit('gift_tier', { threshold: g.threshold, rewardType: g.reward_type });
      }
    });
  }

  function inDrawer(el) { return el && el.closest && el.closest('cart-drawer'); }

  // ---- 1. drawer open / close (class mutation) ----
  var cd = document.querySelector('cart-drawer');
  if (cd) {
    var wasOpen = cd.classList.contains('active');
    new MutationObserver(function () {
      var open = cd.classList.contains('active');
      if (open && !wasOpen) { emit('drawer_open', {}); refreshCart(); scanUpsells(); }
      if (!open && wasOpen) { emit('drawer_close', {}); flush(true); }
      wasOpen = open;
    }).observe(cd, { attributes: true, attributeFilter: ['class'] });
  }

  // ---- 2. clicks (capture phase so we see them regardless of drawer handlers) ----
  var upsellArmedUntil = 0, lastUpsellHandle = null;
  document.addEventListener('click', function (e) {
    var el = e.target && e.target.closest ? e.target.closest('[data-e5-action],[name="checkout"]') : null;
    if (!el || !inDrawer(el)) return;
    var action = el.getAttribute('data-e5-action') || (el.getAttribute('name') === 'checkout' ? 'checkout' : '');

    if (action === 'checkout') { emit('checkout_click', {}); flush(true); return; }

    if (action === 'qty') {
      // stepper: read the target quantity after the drawer applies it
      var lk = el.getAttribute('data-line-key');
      setTimeout(function () {
        var input = document.querySelector('cart-drawer .e5cd__qty-input[data-line-key="' + (window.CSS && CSS.escape ? CSS.escape(lk || '') : lk) + '"], cart-drawer [data-e5-qty-input][data-line-key="' + lk + '"]');
        var q = input ? parseInt(input.value, 10) : parseInt(el.getAttribute('data-to'), 10);
        if (!isNaN(q)) emit('qty_change', { qtyAfter: q, lineKey: lk });
        scheduleCartRefresh();
      }, 50);
      return;
    }
    if (action === 'sub-on') { emit('plan_change', { to: 'sub', sellingPlanId: el.getAttribute('data-plan-id') || null }); scheduleCartRefresh(); return; }
    if (action === 'apply-discount') {
      var di = document.querySelector('cart-drawer [data-e5-discount-input]');
      if (di && di.value.trim()) emit('discount_apply', { code: di.value.trim() });
      scheduleCartRefresh(); return;
    }
    if (action === 'add') {
      var card = el.closest('.e5cd__upsell-card');
      if (card) { emit('upsell_add', { ruleHandle: card.getAttribute('data-rule-handle') || null }); scheduleCartRefresh(); }
      return;
    }
  }, true);

  // ---- 3. changes: qty input + plan/frequency select ----
  document.addEventListener('change', function (e) {
    var t = e.target;
    if (!t || !inDrawer(t)) return;
    if (t.matches && t.matches('[data-e5-qty-input],.e5cd__qty-input')) {
      var q = parseInt(t.value, 10);
      if (!isNaN(q)) emit('qty_change', { qtyAfter: q, lineKey: t.getAttribute('data-line-key') });
      scheduleCartRefresh();
    } else if (t.matches && t.matches('[data-e5-action="plan-select"]')) {
      emit('plan_change', { to: t.value ? 'sub' : 'onetime', sellingPlanId: t.value || null });
      scheduleCartRefresh();
    }
  }, true);

  // ---- 4. submit fallback for checkout ----
  document.addEventListener('submit', function (e) {
    if (e.target && (e.target.id === 'e5cd-checkout-form' || (inDrawer(e.target) && e.target.querySelector('[name="checkout"]')))) {
      emit('checkout_click', {}); flush(true);
    }
  }, true);

  // ---- 5. upsell impressions ----
  var io = ('IntersectionObserver' in window) ? new IntersectionObserver(function (entries) {
    entries.forEach(function (en) {
      if (en.isIntersecting) { emit('upsell_shown', { ruleHandle: en.target.getAttribute('data-rule-handle') || null }); io.unobserve(en.target); }
    });
  }, { threshold: 0.5 }) : null;
  function scanUpsells() {
    if (!io) return;
    document.querySelectorAll('cart-drawer .e5cd__upsell-card').forEach(function (card) {
      if (card.__caSeen) return; card.__caSeen = true; io.observe(card);
    });
  }
  // re-scan when drawer contents change (upsell cards are re-rendered on mutations)
  if (cd) new MutationObserver(function () { scanUpsells(); }).observe(cd, { childList: true, subtree: true });

  // ---- lifecycle ----
  document.addEventListener('visibilitychange', function () { if (document.visibilityState === 'hidden') flush(true); });
  window.addEventListener('pagehide', function () { flush(true); });

  scanUpsells();
})();
